/**
 * Protected Route Component
 * 
 * RESPONSIBILITY: Protect routes that require authentication
 * Optionally requires specific roles
 */

import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../hooks/useAppSelector';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { UserRole } from '../../types/auth.types';
import { hasStaffPortalRole } from '../../utils/staffPortalAccess';
import { AuthGateFallback } from './AuthGateFallback';
import {
  getCurrentUser,
  selectAuthBootstrapComplete,
  selectIsAuthenticated,
  selectSessionMessage,
  selectUser,
} from '../../store/features/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: UserRole[];
  /**
   * Owner dashboard shell: owner + all internal staff roles (same APIs as today).
   * Does not alter layout — only who may open /owner.
   */
  staffPortal?: boolean;
}

const PROFILE_RESOLVE_TIMEOUT_MS = 15_000;

export function ProtectedRoute({
  children,
  requiredRoles = [],
  staffPortal = false,
}: ProtectedRouteProps) {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const bootstrapComplete = useAppSelector(selectAuthBootstrapComplete);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const user = useAppSelector(selectUser);
  const sessionMessage = useAppSelector(selectSessionMessage);
  const [profileTimedOut, setProfileTimedOut] = useState(false);
  const profileFetchStarted = useRef(false);

  const resolvingProfile = bootstrapComplete && isAuthenticated && !user;

  useEffect(() => {
    if (!resolvingProfile) {
      profileFetchStarted.current = false;
      return;
    }
    if (profileTimedOut || profileFetchStarted.current) return;
    profileFetchStarted.current = true;
    dispatch(getCurrentUser());
  }, [dispatch, resolvingProfile, profileTimedOut]);

  useEffect(() => {
    if (!resolvingProfile) {
      setProfileTimedOut(false);
      return;
    }
    const timer = window.setTimeout(() => {
      setProfileTimedOut(true);
    }, PROFILE_RESOLVE_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [resolvingProfile]);

  if (!bootstrapComplete) {
    return <AuthGateFallback message="Checking your session..." />;
  }

  if (profileTimedOut) {
    return (
      <AuthGateFallback
        error="We couldn't load your account. Your session may have expired."
        returnLocation={location}
      />
    );
  }

  if (resolvingProfile) {
    return <AuthGateFallback message="Loading your account..." />;
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        state={{
          from: location,
          message:
            sessionMessage ??
            'Please sign in to access this page.',
        }}
        replace
      />
    );
  }

  if (staffPortal) {
    if (!hasStaffPortalRole(user?.roles)) {
      return <Navigate to="/unauthorized" replace />;
    }
  } else if (requiredRoles.length > 0) {
    const hasRequiredRole = user?.roles?.some((role: UserRole) =>
      requiredRoles.includes(role),
    );

    if (!hasRequiredRole) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
}
